import React from 'react';
import CustomTimePicker from '../../Common/CustomTimePicker';
import CustomDatePicker from '../../Common/CustomDatePicker';
import {
  FigmaAddCircleIcon,
  FigmaMinusCircleIcon,
  FigmaTrashIcon,
  FigmaAddSquareIcon,
  FigmaCalendarOutlineIcon
} from '../../icons';
import SettingsSpaceScopeBar from './SettingsSpaceScopeBar';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const OperatingHoursRow = ({ day, dayNumber, dayHours, onChange, adjustTime }) => {
  const openTime = dayHours.open_time?.substring(0, 5) || '10:00';
  const closeTime = dayHours.close_time?.substring(0, 5) || '23:00';
  return (
    <div className="table-row figma-table-row">
      <div className="table-cell day-cell">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={dayHours.is_open || false}
            onChange={(e) => onChange({ ...dayHours, is_open: e.target.checked })}
          />
          <span className="custom-checkbox"></span>
          <span className="day-label">{day}</span>
        </label>
      </div>
      <div className="table-cell time-cell">
        <CustomTimePicker
          value={openTime}
          onChange={(time) => onChange({ ...dayHours, open_time: time + ':00' })}
          placeholder="Select time"
          className="time-picker-input"
        />
        <div className="time-controls">
          <button type="button" className="time-control-btn" onClick={() => onChange({ ...dayHours, open_time: adjustTime(openTime, -30) + ':00' })} aria-label={`Decrease ${day} opening`}><FigmaMinusCircleIcon /></button>
          <button type="button" className="time-control-btn" onClick={() => onChange({ ...dayHours, open_time: adjustTime(openTime, 30) + ':00' })} aria-label={`Increase ${day} opening`}><FigmaAddCircleIcon /></button>
        </div>
      </div>
      <div className="table-cell time-cell">
        <CustomTimePicker
          value={closeTime}
          onChange={(time) => onChange({ ...dayHours, close_time: time + ':00' })}
          placeholder="Select time"
          className="time-picker-input"
        />
        <div className="time-controls">
          <button type="button" className="time-control-btn" onClick={() => onChange({ ...dayHours, close_time: adjustTime(closeTime, -30) + ':00' })} aria-label={`Decrease ${day} closing`}><FigmaMinusCircleIcon /></button>
          <button type="button" className="time-control-btn" onClick={() => onChange({ ...dayHours, close_time: adjustTime(closeTime, 30) + ':00' })} aria-label={`Increase ${day} closing`}><FigmaAddCircleIcon /></button>
        </div>
      </div>
    </div>
  );
};

const OperatingHoursForm = ({
  operatingHours,
  onOperatingHoursChange,
  operatingHoursPerPerson,
  onOperatingHoursPerPersonChange,
  holidays,
  onHolidayDelete,
  draftHolidays,
  onNewHolidayChange,
  addAnotherHoliday,
  removeDraftHoliday,
  venueId,
  venueSpaces = [],
  settingsSpaceId,
  onSettingsSpaceIdChange,
  multiSpace = false,
  scopeLoading = false,
  ownerEmail = '',
  onNotify
}) => {
  const adjustTime = (timeStr, deltaMinutes) => {
    const base = timeStr || '00:00';
    const [hours, minutes] = base.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return base;
    }
    const totalMinutes = hours * 60 + minutes + deltaMinutes;
    const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const nextHours = Math.floor(normalized / 60);
    const nextMinutes = normalized % 60;
    return `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`;
  };

  const formatHolidayDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <div className="settings-panel operating-hours-panel" style={{ width: '100%' }}>
      <div className="operating-hours-header">
        <h3>Operating hours and holidays</h3>
      </div>
      {multiSpace ? (
        <SettingsSpaceScopeBar
          spaces={venueSpaces}
          value={settingsSpaceId}
          onChange={onSettingsSpaceIdChange}
        />
      ) : null}
      {scopeLoading ? (
        <p className="pricing-helper-text">Loading hours for selected space…</p>
      ) : null}
      <div className="operating-section">
        <label className="operating-section-title">Operating hours (full venue hire)</label>
        <div className="operating-hours-table figma-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">DAYS OPEN</div>
            <div className="table-cell">OPEN FROM</div>
            <div className="table-cell">OPEN TO</div>
          </div>
          {DAYS_OF_WEEK.map((day, index) => {
            const dayNumber = index + 1;
            const dayHours = operatingHours[dayNumber] || { is_open: false, open_time: '10:00:00', close_time: '23:00:00' };
            return (
              <OperatingHoursRow
                key={day}
                day={day}
                dayNumber={dayNumber}
                dayHours={dayHours}
                onChange={(updated) => onOperatingHoursChange({ ...operatingHours, [dayNumber]: updated })}
                adjustTime={adjustTime}
              />
            );
          })}
        </div>
      </div>

      <div className="operating-section">
        <label className="operating-section-title">Per-person operating hours</label>
        <p className="pricing-helper-text" style={{ fontSize: '14px', color: '#585858', marginBottom: '12px' }}>
          Can differ from full venue hours. By default same as full venue. Used for per-person bookings.
        </p>
        <div className="operating-hours-table figma-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">DAYS OPEN</div>
            <div className="table-cell">OPEN FROM</div>
            <div className="table-cell">OPEN TO</div>
          </div>
          {DAYS_OF_WEEK.map((day, index) => {
            const dayNumber = index + 1;
            const dayHours = operatingHoursPerPerson?.[dayNumber] || operatingHours[dayNumber] || { is_open: false, open_time: '10:00:00', close_time: '23:00:00' };
            return (
              <OperatingHoursRow
                key={`per-person-${day}`}
                day={day}
                dayNumber={dayNumber}
                dayHours={dayHours}
                onChange={(updated) => onOperatingHoursPerPersonChange?.({ ...operatingHoursPerPerson, [dayNumber]: updated })}
                adjustTime={adjustTime}
              />
            );
          })}
        </div>
      </div>

      <div className="operating-section">
        <label className="operating-section-title">Holidays</label>
        <div className="holidays-table figma-table">
          <div className="table-header figma-table-header">
            <div className="table-cell">HOLIDAY NAME</div>
            <div className="table-cell">DATE</div>
            <div className="table-cell action-cell"></div>
          </div>
          {holidays.map((holiday) => (
            <div key={holiday.id} className="table-row figma-table-row">
              <div className="table-cell">
                {holiday.holiday_name || holiday.name}
              </div>
              <div className="table-cell holiday-date-cell holiday-date-cell-saved">
                <FigmaCalendarOutlineIcon className="icon-calendar" />
                <span>{formatHolidayDate(holiday.holiday_date || holiday.date)}</span>
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  onClick={() => onHolidayDelete(holiday.id)}
                  type="button"
                  aria-label="Delete holiday"
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          {draftHolidays.map((draft, index) => (
            <div key={`draft-${index}`} className="table-row figma-table-row add-holiday-draft-row">
              <div className="table-cell">
                <input
                  type="text"
                  placeholder="Enter holiday"
                  className="form-input "
                  value={draft.name}
                  onChange={(e) =>
                    onNewHolidayChange(index, { name: e.target.value, date: draft.date || '' })
                  }
                />
              </div>
              <div className="table-cell holiday-date-cell holiday-date-cell-picker">
                <CustomDatePicker
                  value={draft.date}
                  onChange={(date) => onNewHolidayChange(index, { name: draft.name || '', date })}
                  placeholder="Select date"
                  className="holiday-date-picker"
                />
              </div>
              <div className="table-cell action-cell">
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Remove holiday"
                  onClick={() => removeDraftHoliday(index)}
                >
                  <FigmaTrashIcon className="icon-trash" />
                </button>
              </div>
            </div>
          ))}
          <div className="table-row add-holiday-row">
            <button className="add-holiday-btn" type="button" onClick={addAnotherHoliday}>
              <FigmaAddSquareIcon className="icon-add-square" />
              Add another holiday
            </button>
          </div>
        </div>
      </div>

      {multiSpace ? (
        <p className="pricing-helper-text" style={{ marginTop: '24px' }}>
          Link each space to a Google calendar under{' '}
          <strong>Settings → Calendar sync</strong>.
        </p>
      ) : null}
    </div>
  );
};

export default OperatingHoursForm;

